import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { RequestState } from '../../models/RequestState';
import { CoreGoogleDeviceDashboard } from '../../models/CoreDeviceModels';
import { GetGoogleDeviceDashboardInfo } from '../../store/GoogleDevice/GoogleDeviceDashboardAction';
import StatusCode from '../../util/StatusCode';


interface GoogleDeviceOverviewProps {
    displayTitle: boolean
}

type CountSelector = (data: CoreGoogleDeviceDashboard) => number;

interface Info {
    key: string
    name: string
    link?: string
    selector: CountSelector
    title?: string
    displayProcent?: boolean
}

const GoogleDeviceOverview = (props: GoogleDeviceOverviewProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(['common', 'device']);

    const dashboard = useSelector<RootState, RequestState<CoreGoogleDeviceDashboard>>(x => x.deviceDashboard.dashboard);

    useEffect(() => {
        GetGoogleDeviceDashboardInfo()(dispatch);
    }, [dispatch]);

    const getCount = (selector: CountSelector): number =>
        dashboard.code === StatusCode.COMPLETE && dashboard.data ? selector(dashboard.data) : 0;

    const versionLinks = dashboard.code === StatusCode.COMPLETE && dashboard.data.approvedVersions
        ? dashboard.data.approvedVersions.map(x => `osVersion=${x}`).join("&") : "";

    const osVersionPercentLink = versionLinks
        ? `/device/googleDevices?${versionLinks}&status=1`
        : '/device/googleDevices?status=1';

    const info: Info[] = [
        { key: 'activeDevices', name: t('dashboard.info.active', { ns: 'device' }), link: '/device/googleDevices?status=1', selector: d => d.activeDevices },
        { key: 'inActiveDevices', name: t('dashboard.info.inactive', { ns: 'device' }), link: '/device/googleDevices?status=6', selector: d => d.inactiveDevices },
        { key: 'osVersionPercent', name: `${t('dashboard.info.osVersionPercent', { ns: 'device' })}`, link: osVersionPercentLink, selector: d => d.osVersionPercent, displayProcent: true },
        { key: 'autoUpdateExpiration', name: t('dashboard.info.autoUpdateExpiration', { ns: 'device' }), title: t('dashboard.info.autoUpdateExpirationDescription', { ns: 'device' }), selector: d => d.autoUpdateExpiration },
        { key: 'lastSync', name: t('dashboard.info.lastSync', { ns: 'device' }), title: t('dashboard.info.lastSyncDescription', { ns: 'device' }), link: '/device/googleDevices?lastSync=30&status=1', selector: d => d.lastSync },
    ];

    return (<>
        {props.displayTitle && <h3>{t('dashboard.info.title', { ns: 'device' })}</h3>}
        <div className="list-group list-group-horizontal-lg mb-3">
            {info.map(entry =>
                entry.link ? (
                    <Link to={entry.link} key={entry.key} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center flex-fill">
                        <span title={entry?.title}>{entry.name}</span>
                        <span className="badge bg-primary rounded-pill">{getCount(entry.selector)}{entry.displayProcent ? ' %' : null}</span>
                    </Link>)
                    : (<div key={entry.key} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center flex-fill">
                        {entry.name}
                        <span className="badge bg-primary rounded-pill">{getCount(entry.selector)}</span>
                    </div>)
            )}
        </div>
    </>)
}

export default GoogleDeviceOverview;
